import isNil from 'lodash/isNil';
import isArray from 'lodash/isArray';
import isString from 'lodash/isString';
import isEmpty from 'lodash/isEmpty';
import isNumber from 'lodash/isNumber';
import moment, { Moment } from 'moment';
import { defineMessages } from 'react-intl';
import { isValidHexColor } from '@lifeomic/chroma-react/components/ColorPicker';

const regexTest = (
  value: string,
  expressions: RegExp[],
  errorMessage: string,
) => {
  if (!value) return undefined;
  return expressions.some(exp => exp.test(value)) ? undefined : errorMessage;
};

export type ValidationSignature = (value: any) => string;

export interface Validators {
  required: ValidationSignature;
  htmlInjection: ValidationSignature;
  phoneNumber: ValidationSignature;
  intlPhoneNumber: ValidationSignature;
  socialSecurityNumber: ValidationSignature;
  email: ValidationSignature;
  postalCode: ValidationSignature;
  greaterThan: (threshold: number) => ValidationSignature;
  date: (format: string) => ValidationSignature;
  validMoment: ValidationSignature;
  validJSON: ValidationSignature;
  numeric: ValidationSignature;
  maxLength: (maxLength: number) => ValidationSignature;
  alphaAndSpace: ValidationSignature;
  hexColor: ValidationSignature;
}

export const countryCodePhoneNumberFormat = /^\+1 \(\d{3}\) \d{3}-\d{4}$/; // +1 (123) 456-7890

const reg = {
  phoneNumber: [
    countryCodePhoneNumberFormat,
    /^\(\d{3}\) \d{3}-\d{4}$/, // (123) 456-7890
    /^\d{3} \d{3}-\d{4}$/, // 123 456-7890
    /^\d{3} \d{3} \d{4}$/, // 123 456 7890
    /^\d{3} \d{7}$/, // 123 4567890
    /^\d{10}$/, // 1234567890
  ],
  intlPhoneNumber: [/^\d{7,15}$/], // International spec limits phone number lengths (w/ code) to 7-15 digits
  socialSecurityNumber: [/^\d{3}-\d{2}-\d{4}$/],
  email: [
    /^[a-z0-9!#$%&'*+/=?^_`{|}~-]+(?:\.[a-z0-9!#$%&''*+/=?^_`{|}~-]+)*@(?:[a-z0-9](?:[a-z0-9-]*[a-z0-9])?\.)+[a-z0-9](?:[a-z0-9-]*[a-z0-9])?$/i,
  ],
  htmlInjection: [/^[^<>&/`@]+$/],
  postalCode: [/^[0-9]{5}(?:-[0-9]{4})?$/],
};

const messages = defineMessages({
  required: {
    id: 'validation.required',
    defaultMessage: 'Field is required',
  },
  email: {
    id: 'validation.email',
    defaultMessage: 'Must be a valid email (ex: john.smith@email.com)',
  },
  htmlInjection: {
    id: 'validation.htmlInjection',
    defaultMessage: 'The following characters are not allowed: < > & / ` @',
  },
  phoneNumber: {
    id: 'validation.phoneNumber',
    defaultMessage:
      'Must be a valid phone number (ex: "1234567890",  "123 4567890", "(123) 456-7890", or "+1 (123) 456-7890")',
  },
  intlPhoneNumber: {
    id: 'validation.intlPhoneNumber',
    defaultMessage:
      'Must be a valid international phone number, no extensions (7 digit min length, 15 digit max length, accepts 0-9, +, -, ., ())',
  },
  socialSecurityNumber: {
    id: 'validation.socialSecurityNumber',
    defaultMessage:
      'Must be a valid social security number (ex: "123-45-6789")',
  },
  postalCode: {
    id: 'validation.postalCode',
    defaultMessage:
      'Must be a valid ZIP or ZIP+4 format (ex: "12345" or "12345-6789")',
  },
  greaterThan: {
    id: 'validation.greaterThan',
    defaultMessage: 'Must be greater than {threshold}',
  },
  invalidDate: {
    id: 'validation.invalidDate',
    defaultMessage: 'Date must be a valid date in the format of {format}',
  },
  invalidMoment: {
    id: 'validation.invalidMoment',
    defaultMessage: 'Date is not valid',
  },
  invalidJSON: {
    id: 'validation.invalidJSON',
    defaultMessage: 'JSON is not valid',
  },
  numeric: {
    id: 'validation.numeric',
    defaultMessage: 'Value must be numeric',
  },
  maxLength: {
    id: 'validation.maxLength',
    defaultMessage: 'Value cannot be longer than {maxLength} characters.',
  },
  alphaAndSpace: {
    id: 'validation.alphaAndSpace',
    defaultMessage: 'Value cannot contain and special characters.',
  },
  hexColor: {
    id: 'validation.hexColor',
    defaultMessage: 'Value must be a valid hex color code.',
  },
});

export default (formatMessage: any) =>
  ({
    required: (value: any) => {
      const emptyArray = isArray(value) && value.length === 0;
      const emptyValue = isNil(value);
      const emptyString = isString(value) && isEmpty(value);
      if (emptyArray || emptyValue || emptyString) {
        return formatMessage(messages.required);
      }
    },
    phoneNumber: (value: any) => {
      if (isNil(value) || !isString(value)) return;

      return regexTest(
        value,
        reg.phoneNumber,
        formatMessage(messages.phoneNumber),
      );
    },
    intlPhoneNumber: (value: any) => {
      if (isNil(value) || !isString(value)) return;

      const digitsOnly = value.replace(/[\D]/g, '');
      if (digitsOnly.length === 0)
        return formatMessage(messages.intlPhoneNumber);
      return regexTest(
        digitsOnly,
        reg.intlPhoneNumber,
        formatMessage(messages.intlPhoneNumber),
      );
    },
    socialSecurityNumber: (value: string) => {
      if (isNil(value) || !isString(value)) return;

      return regexTest(
        value,
        reg.socialSecurityNumber,
        formatMessage(messages.socialSecurityNumber),
      );
    },
    email: (value: any) => {
      if (isNil(value) || !isString(value)) return;

      return regexTest(value, reg.email, formatMessage(messages.email));
    },
    htmlInjection: (value: any) => {
      if (isNil(value) || !isString(value)) return;

      return regexTest(
        value,
        reg.htmlInjection,
        formatMessage(messages.htmlInjection),
      );
    },
    postalCode: (value: any) => {
      if (isNil(value) || !isString(value)) return;

      return regexTest(
        value,
        reg.postalCode,
        formatMessage(messages.postalCode),
      );
    },
    greaterThan: (threshold: number) => (value: any) => {
      if (
        isNil(value) ||
        !(
          isNumber(value) ||
          (isString(value) &&
            !isNaN(parseFloat(value)) &&
            isFinite(value as any))
        )
      )
        return;

      if (value <= threshold) {
        return formatMessage(messages.greaterThan, { threshold });
      }
    },
    date: (format: string) => (value: any) => {
      if (isNil(value) || !isString(value) || !value) return;

      const asMoment = moment(value, format, true);

      if (
        !asMoment ||
        !asMoment?.isValid() ||
        asMoment.format(format) !== value
      ) {
        return formatMessage(messages.invalidDate, { format });
      }
    },
    validMoment: (value: Moment) => {
      if (value && (!moment.isMoment(value) || !value?.isValid?.())) {
        return formatMessage(messages.invalidMoment);
      }
    },
    validJSON: (value: string) => {
      try {
        JSON.parse(value);
      } catch (e) {
        return formatMessage(messages.invalidJSON);
      }
    },
    numeric: (value: any) => {
      if (isNil(value)) return;

      if (isNumber(value)) return;

      if (
        isString(value) &&
        !isNaN(parseFloat(value)) &&
        isFinite(value as any)
      ) {
        return;
      }

      return formatMessage(messages.numeric);
    },
    maxLength: (maxLength: number) => (value: string) => {
      if (isNil(value)) return;

      if (value.length > maxLength) {
        return formatMessage(messages.maxLength, { maxLength });
      }
      return;
    },
    alphaAndSpace: (value: string) => {
      if (isNil(value)) return;
      if (!/^[a-zA-Z\d\s]*$/i.test(value)) {
        return formatMessage(messages.alphaAndSpace);
      }
    },
    hexColor: (value: string) => {
      if (isNil(value)) return;
      if (!isValidHexColor(value)) {
        return formatMessage(messages.hexColor);
      }
    },
  } as Validators);

export const combineValidators =
  (...validators: ValidationSignature[]) =>
  (value: any) => {
    for (let i = 0; i < validators.length; i++) {
      const validator = validators[i];
      if (validator) {
        const validationRes = validator(value);
        if (validationRes) return validationRes;
      }
    }
  };
